/**
 * Centralized filter counting utility for search pages.
 * Single source of truth for which filters are countable and how to count them.
 *
 * @module utils/filterCount
 */
import {
	newAmenities,
	defaultVehicleAmenitiesAndRequirements,
} from '../config/units';
import {
	getSqftForSize,
	getFilterSqftForSize,
} from '../lib/search/parseAmenityFromPath';

/**
 * Extract countable amenity key from an amenity config object.
 * Supports both 'value' (newAmenities) and 'checkboxQueryParam' (legacy) properties.
 *
 * @param {Object} amenity - Amenity configuration object
 * @returns {string} The amenity key used in search params
 */
const getAmenityKey = (amenity) => amenity.checkboxQueryParam || amenity.value;

/**
 * Storage amenity keys derived from newAmenities (the UI source of truth).
 * Includes all 13 amenities displayed in SearchFilters.
 * @type {string[]}
 */
export const storageAmenityKeys = newAmenities.map(getAmenityKey);

/**
 * Legacy amenity keys that are no longer displayed in SearchFilters UI but
 * are still valid search params (see lib/search/index.js allowedSearchParams).
 * These may exist in bookmarked URLs or external links, so we count them
 * for backwards compatibility.
 * @type {string[]}
 */
export const legacyAmenityKeys = [
	'alarm',
	'cameras',
	'getOpenNow',
	'fencedAndLit',
];

/**
 * Vehicle filter keys derived from defaultVehicleAmenitiesAndRequirements.
 * @type {string[]}
 */
export const vehicleFilterKeys =
	defaultVehicleAmenitiesAndRequirements.map(getAmenityKey);

/**
 * All countable amenity keys - union of storage, vehicle, and legacy keys.
 * Used to ensure any active filter triggers Clear All, regardless of search type.
 * @type {string[]}
 */
export const allAmenityKeys = [
	...new Set([
		...storageAmenityKeys,
		...vehicleFilterKeys,
		...legacyAmenityKeys,
	]),
];

/**
 * Valid vehicle types that can appear in the vehicleTypes param.
 * These correspond to the Car/Boat/RV checkboxes in SearchFilters.
 * @type {string[]}
 */
export const validVehicleTypes = ['car', 'boat', 'rv'];

export const nearMeAmenityKeyMap = {
	'24-hour-storage': 'twentyFourHourAccess',
	'climate-controlled-storage': 'climateControlled',
	'drive-up-storage': 'driveUp',
	'storage-with-electricity': 'power',
	'wine-storage': 'onlyWine',
	'storage-with-free-truck-rental': 'freeTruckRental',
	'mini-storage': '0-25',
	'locker-storage': '0-5',
	'5x5-storage': '26-75',
	'5x10-storage': '26-75',
	'5x15-storage': '76-200',
	'10x10-storage': '76-200',
	'10x15-storage': '76-200',
	'10x20-storage': '201-999',
	'10x25-storage': '201-999',
	'10x30-storage': '201-999',
};

export const nearMeVehicleKeyMap = {
	'boat-storage': 'boat',
	'car-storage': 'car',
	'rv-storage': 'rv',
};

/**
 * Adds a slug-implied vehicle type to a comma-separated vehicleTypes string,
 * keeping any other types already present. No-op if `implied` is falsy or
 * already included.
 *
 * @param {string} [vehicleTypesStr]
 * @param {string} [implied]
 * @returns {string}
 */
export function vehicleTypesWith(vehicleTypesStr, implied) {
	const types = (vehicleTypesStr || '')
		.split(',')
		.map((t) => t.trim())
		.filter(Boolean);
	if (implied && !types.includes(implied)) {
		types.push(implied);
	}
	return types.join(',');
}

/**
 * Removes a slug-implied vehicle type from a comma-separated vehicleTypes
 * string, keeping any other types the user selected on top of it. Returns
 * `undefined` (rather than an empty string) when nothing remains, so callers
 * can tell "drop this param" apart from "set it to an empty value".
 *
 * @param {string} [vehicleTypesStr]
 * @param {string} [implied]
 * @returns {string|undefined}
 */
export function vehicleTypesWithout(vehicleTypesStr, implied) {
	if (!implied || !vehicleTypesStr) return undefined;
	const remaining = vehicleTypesStr
		.split(',')
		.map((t) => t.trim())
		.filter((t) => t && t !== implied);
	return remaining.length > 0 ? remaining.join(',') : undefined;
}

export const NEAR_ME_KEYWORD_TO_SIZE = {
	'5x5-storage': '5x5',
	'5x10-storage': '5x10',
	'5x15-storage': '5x15',
	'10x10-storage': '10x10',
	'10x15-storage': '10x15',
	'10x20-storage': '10x20',
	'10x25-storage': '10x25',
	'10x30-storage': '10x30',
	'mini-storage': '0-25',
	'locker-storage': '0-5',
};

/**
 * Derives a near-me size keyword's CLP-equivalent size slug, so the sqft
 * range implied by the keyword can be resolved via SIZE_SLUG_TO_SQFT_MAP /
 * SIZE_SLUG_TO_FILTER_SQFT_MAP (see getNearMeFilterParams below). Derived
 * rather than hand-mapped so there's no fourth list of the same nine size
 * keywords (see storageUnitKeys/NEAR_ME_KEYWORD_TO_SIZE above) to keep in
 * sync by hand.
 *
 * `mini-storage` is the one keyword that maps to itself rather than
 * `${keyword}-units`. `locker-storage` has no sqft-range equivalent (it uses
 * the onlyLocker filter instead), so it deliberately resolves to `undefined`
 * — getNearMeFilterParams treats that as "implied value unknown" and keeps
 * any explicit sqft on pagination rather than dropping it.
 *
 * @param {string} keyword - A near-me size keyword (must be in storageUnitKeys)
 * @returns {string|undefined}
 */
function getClpSizeSlugForNearMeKeyword(keyword) {
	if (keyword === 'mini-storage') return 'mini-storage';
	if (keyword === 'locker-storage') return undefined;
	return `${keyword}-units`;
}

export const vehicleUnitSizeFilterValues = [
	'15',
	'20',
	'25',
	'30',
	'35',
	'40',
	'50',
];

const aminityKeys = [
	'24-hour-storage',
	'climate-controlled-storage',
	'drive-up-storage',
	'storage-with-electricity',
	'wine-storage',
	'storage-with-free-truck-rental',
];

const storageUnitKeys = [
	'5x5-storage',
	'5x10-storage',
	'5x15-storage',
	'10x10-storage',
	'10x15-storage',
	'10x20-storage',
	'10x25-storage',
	'10x30-storage',
	'mini-storage',
	'locker-storage',
];

/**
 * Given a near-me keyword slug, returns the search param(s) that the slug already
 * implies, so they can be omitted from self-referential URLs (pagination links,
 * canonical) and restored when re-fetching a paginated near-me page.
 *
 * @param {string} keyword - The near-me URL keyword (e.g., "climate-controlled-storage")
 * @returns {{ paramsToOmit: string[], restore: Object }|null}
 */
export function getNearMeFilterParams(keyword) {
	if (!keyword) return null;

	if (aminityKeys.includes(keyword)) {
		const paramKey = nearMeAmenityKeyMap[keyword];
		// `amenity` is the slug-style value (e.g. "climate-controlled") echoed back
		// alongside the boolean filter param — both are implied by the slug, so both
		// must be omitted from self-referential URLs.
		return {
			paramsToOmit: [paramKey, 'amenity'],
			restore: { [paramKey]: 'true' },
		};
	}

	if (nearMeVehicleKeyMap[keyword]) {
		// `vehicleTypes` is the checkbox-filter param SearchFilters/SearchPageWrapper
		// derive from the path-implied vehicle (see SearchPageWrapper's
		// `queryParams.vehicleTypes = vehicleFromPath`) — also implied by the slug,
		// so it must be omitted/restored alongside `vehicle`/`searchType`.
		return {
			paramsToOmit: ['vehicle', 'vehicleTypes', 'searchType'],
			restore: {
				vehicle: nearMeVehicleKeyMap[keyword],
				vehicleTypes: nearMeVehicleKeyMap[keyword],
				searchType: 'vehicle',
			},
		};
	}

	if (storageUnitKeys.includes(keyword)) {
		// getNearMePages sends the slug-derived label as `size` (see
		// NEAR_ME_KEYWORD_TO_SIZE / store/search/slice.js), not `sqft` — but the
		// API response echoes back a resolved `sqft` range (e.g. "20-37") into
		// searchParams, which is also implied by the slug and must be omitted
		// from self-referential URLs too. Don't restore it on pagination
		// fetches: there's no reliable way to recompute the exact resolved
		// range here, and `size` alone is enough for the backend to reapply
		// the same filter (see getNearMePages).
		//
		// impliedSqftValues lets callers (see SearchPagination) tell an echoed
		// slug default apart from a real user-chosen size override — checked
		// against both known sqft vocabularies (see getClpSizeSlugForNearMeKeyword)
		// since it's not confirmed which one the API echoes back. Empty for
		// `locker-storage` (no CLP-slug equivalent), which callers should treat
		// as "unknown" and keep any explicit sqft rather than drop it.
		const clpSlug = getClpSizeSlugForNearMeKeyword(keyword);
		const impliedSqftValues = clpSlug
			? [getSqftForSize(clpSlug), getFilterSqftForSize(clpSlug)].filter(Boolean)
			: [];
		return {
			paramsToOmit: ['size', 'sqft'],
			restore: { size: NEAR_ME_KEYWORD_TO_SIZE[keyword] },
			impliedSqftValues,
		};
	}

	if (keyword === 'storage-deals') {
		return { paramsToOmit: ['dealsFilter'], restore: { dealsFilter: 'all' } };
	}

	return null;
}

/**
 * Calculate total filter count from search params.
 *
 * Counted filters:
 * - ALL amenity/requirement checkboxes (storage, vehicle, legacy) - counted regardless of searchType
 * - Vehicle type selections (car, boat, rv) - each counts as 1, only for vehicle searchType
 * - Vehicle accommodation selection (outdoor-covered, outdoor-uncovered)
 * - Price filter (minPrice or maxPrice counts as 1)
 * - Specials/deals filter (hasSpecial)
 * - Review filter (averageReview)
 * - Unit size filters (sqft or onlyLocker count as ONE filter for storage)
 * - Vehicle length filter (minLength/maxLength counts as 1, only when no vehicleTypes selected)
 * - Near-me pre-selected amenity or vehicle (counts as 1 if not already in searchParams)
 *
 * @param {Object} searchParams - Current search parameters object
 * @param {string} [searchType='storage'] - Either 'storage' or 'vehicle'
 * @param {Object} [nearMeParams={}] - Near-me params from URL (storageAmenity, vehicle)
 * @returns {number} Total count of active filters
 */
export function calculateFilterCount(
	searchParams,
	searchType = 'storage',
	nearMeParams = {},
	location = {},
) {
	if (!searchParams) return 0;

	let count = 0;

	// 1. Count ALL amenity/requirement filters that are active
	// Using combined keys ensures Clear All activates regardless of how filters were set
	// (e.g., storage filters that persist after switching to vehicle search)
	if (location?.search) {
		allAmenityKeys.forEach((key) => {
			if (
				searchParams[key] === 'true' &&
				!(
					nearMeParams.storageAmenity &&
					nearMeAmenityKeyMap[nearMeParams.storageAmenity] === key
				)
			) {
				count++;
			}
		});
	}

	// 2. Vehicle type selections (car, boat, rv checkboxes)
	// Each selected type counts as 1 filter.
	if (location?.search && searchType === 'vehicle') {
		if (searchParams.vehicleTypes) {
			const selectedTypes = searchParams.vehicleTypes
				.split(',')
				.map((t) => t.trim());
			let validSelectedTypes;
			if (nearMeParams.vehicle) {
				validSelectedTypes = selectedTypes.filter(
					(t) =>
						validVehicleTypes.includes(t) &&
						t !== nearMeVehicleKeyMap[nearMeParams.vehicle],
				);
			} else {
				validSelectedTypes = selectedTypes.filter((t) =>
					validVehicleTypes.includes(t),
				);
			}
			count += validSelectedTypes.length;
		}
	}

	// 3. Vehicle accommodation (outdoor-covered, outdoor-uncovered)
	if (searchType === 'vehicle' && searchParams.vehicleAccommodation) {
		count++;
	}

	// 4. Price filter (slider sets both min and max, count as 1 filter)
	if (searchParams.minPrice || searchParams.maxPrice) {
		count++;
	}

	// 5. Special/deals filter
	if (searchParams.hasSpecial) {
		count++;
	}

	// 6. Review filter
	if (searchParams.averageReview) {
		count++;
	}

	// 7. Unit size filters
	// For storage: sqft or onlyLocker count as ONE filter
	if (
		location?.search &&
		!NEAR_ME_KEYWORD_TO_SIZE[nearMeParams.storageAmenity] &&
		(searchParams.sqft || searchParams.onlyLocker)
	) {
		count++;
	}

	// 8. Vehicle length filter
	// Only count if no vehicleTypes are selected - when vehicleTypes is set,
	// minLength/maxLength are auto-derived from the vehicle type selection
	// and counting them separately would be double-counting
	if (
		searchType === 'vehicle' &&
		searchParams.minLength &&
		vehicleUnitSizeFilterValues.includes(searchParams.minLength)
	) {
		count++;
	}

	// 9. Near-me pre-selected amenity — count only if not already reflected in searchParams
	if (nearMeParams.storageAmenity) {
		if (
			aminityKeys.includes(nearMeParams.storageAmenity) ||
			nearMeVehicleKeyMap[nearMeParams.storageAmenity]
		) {
			count++;
		}
		if (
			!searchParams.minLength &&
			storageUnitKeys.includes(nearMeParams.storageAmenity)
		) {
			count++;
		}
	}

	return count;
}
